import { supabase } from '@/integrations/supabase/client';
import type { Membership, ClaimResult } from '@/contracts/membership';

const FOUNDER_CHECK_KEY = 'founder_check_pending';

export const membershipService = {
  /**
   * Get current user's membership. Returns null if not found.
   */
  async getMembership(userId: string): Promise<Membership | null> {
    const { data, error } = await supabase
      .from('memberships')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching membership:', error);
      return null;
    }
    
    return data as Membership | null;
  },

  /**
   * Atomic founder claim via RPC. Only calls once per browser session.
   */
  async ensureFounder(): Promise<ClaimResult> {
    // Check localStorage flag to prevent duplicate calls
    const alreadyChecked = localStorage.getItem(FOUNDER_CHECK_KEY);
    if (alreadyChecked) {
      return 'exists';
    }

    // Set pending flag
    localStorage.setItem(FOUNDER_CHECK_KEY, 'true');

    try {
      const { data, error } = await supabase.rpc('claim_founder_membership');
      
      if (error) {
        console.error('Founder claim error:', error);
        localStorage.removeItem(FOUNDER_CHECK_KEY);
        return 'sold_out';
      }

      return data as ClaimResult;
    } catch (err) {
      console.error('Founder claim exception:', err);
      localStorage.removeItem(FOUNDER_CHECK_KEY);
      return 'sold_out';
    }
  },

  /**
   * Check if user has Pro-level access (founder or pro)
   */
  isPro(membership: Membership | null): boolean {
    return membership?.tier === 'pro' || membership?.tier === 'founder';
  },

  /**
   * Check if user is a founder
   */
  isFounder(membership: Membership | null): boolean {
    return membership?.tier === 'founder';
  },

  /**
   * Get current count of founder memberships
   */
  async getFounderCount(): Promise<number> {
    const { count, error } = await supabase
      .from('memberships')
      .select('*', { count: 'exact', head: true })
      .eq('tier', 'founder');
    
    if (error) {
      console.error('Error fetching founder count:', error);
      return 0;
    }
    
    return count || 0;
  },

  /**
   * Get remaining founder spots (out of 500)
   */
  async getRemainingSpots(): Promise<number> {
    const count = await this.getFounderCount();
    return Math.max(0, 500 - count);
  },

  /**
   * Clear the founder check flag (for testing)
   */
  clearFounderCheckFlag(): void {
    localStorage.removeItem(FOUNDER_CHECK_KEY);
  },

  /**
   * Get AskCircle usage stats for a user
   */
  async getUsageStats(userId: string): Promise<{
    dailyCount: number;
    dailyLimit: number;
    monthlyCount: number;
    monthlyLimit: number;
    tier: string;
  } | null> {
    const { data: membership } = await supabase
      .from('memberships')
      .select('tier')
      .eq('user_id', userId)
      .single();

    const tier = membership?.tier || 'free';

    // Get quota config
    const { data: config } = await supabase
      .from('system_config')
      .select('value')
      .eq('key', 'askcircle_quotas')
      .single();

    const quotas = config?.value as any || {
      free: { daily_limit: 5, monthly_limit: 50 },
      pro: { daily_limit: 100, monthly_limit: 2000 },
      founder: { daily_limit: -1, monthly_limit: -1 }
    };
    const tierQuota = quotas[tier] || quotas.free;

    // Get today's usage
    const today = new Date().toISOString().split('T')[0];
    const { data: dailyUsage } = await supabase
      .from('askcircle_usage')
      .select('message_count')
      .eq('user_id', userId)
      .eq('period_start', today)
      .maybeSingle();

    // Get this month's usage
    const monthStart = new Date();
    monthStart.setDate(1);
    const { data: monthlyUsage } = await supabase
      .from('askcircle_usage')
      .select('message_count')
      .gte('period_start', monthStart.toISOString().split('T')[0]);

    const monthlyCount = monthlyUsage?.reduce((sum, day) => sum + (day.message_count || 0), 0) || 0;

    return {
      dailyCount: dailyUsage?.message_count || 0,
      dailyLimit: tierQuota.daily_limit,
      monthlyCount,
      monthlyLimit: tierQuota.monthly_limit,
      tier
    };
  },

  /**
   * Get total AskCircle usage across all users (admin stats)
   */
  async getTotalUsageStats(): Promise<{
    totalMessages: number;
    todayMessages: number;
    byTier: { free: number; pro: number; founder: number };
  }> {
    const today = new Date().toISOString().split('T')[0];

    // Get today's total
    const { data: todayData } = await supabase
      .from('askcircle_usage')
      .select('message_count')
      .eq('period_start', today);

    const todayMessages = todayData?.reduce((sum, row) => sum + (row.message_count || 0), 0) || 0;

    // Get all-time total (last 30 days for performance)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const { data: totalData } = await supabase
      .from('askcircle_usage')
      .select('message_count')
      .gte('period_start', thirtyDaysAgo.toISOString().split('T')[0]);

    const totalMessages = totalData?.reduce((sum, row) => sum + (row.message_count || 0), 0) || 0;

    // Get breakdown by tier (simplified - just count today's messages by tier)
    const { data: usersData } = await supabase
      .from('askcircle_usage')
      .select('user_id, message_count')
      .eq('period_start', today);

    const userIds = usersData?.map(u => u.user_id) || [];
    const { data: memberships } = await supabase
      .from('memberships')
      .select('user_id, tier')
      .in('user_id', userIds);

    const byTier = { free: 0, pro: 0, founder: 0 };
    usersData?.forEach(usage => {
      const membership = memberships?.find(m => m.user_id === usage.user_id);
      const tier = (membership?.tier || 'free') as 'free' | 'pro' | 'founder';
      byTier[tier] += usage.message_count || 0;
    });

    return {
      totalMessages,
      todayMessages,
      byTier
    };
  },
};
